import React, { useEffect, useMemo, useState } from 'react'
import { Grid, Paper, Stack, Typography } from '@mui/material'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import { getStrategyServerUrl } from '../../utils/api/servers'
import { isEmptyString } from '../../utils/string-utils'
import Loader from '../../components/loader/loader'
import StackRowLayout from '../../layouts/stack-row-layout'
import StrategyComponent from '../strategy/strategy-component'
import TradeCardList from '../trade/trade-card-list'
import BookCardList from '../book/book-card-list'
import EventDataGrid from '../event/event-data-grid'

export enum DISPLAY_TYPE {
  DETAILED = 'Detailed',
  DETAILED_NO_TRADES = 'Detailed + No Trades Display',
  SIMPLE = 'Simple',
  SIMPLE_NO_TRADES = 'Simple + No Trades Display',
}

interface StrategyTradeBookLayoutProps {
  strategyId: string
  strategyName: string
  displayType?: DISPLAY_TYPE
}

const StrategyTradeBookComposite: React.FunctionComponent<StrategyTradeBookLayoutProps> = ({
  strategyId, strategyName, displayType = DISPLAY_TYPE.DETAILED,
}) => {
  // Connection States
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [attemptedApiConnect, setAttemptedApiConnect] = useState<boolean>(false)
  const [apiConnected, setApiConnected] = useState<boolean>(false)

  // Data URLs
  const [streamUrl, setStreamUrl] = useState<string | null>(null)

  useEffect(() => {
    const invalidStrategyId = isEmptyString(strategyId)

    if (invalidStrategyId) {
      const isDevNodeEnv = process.env.NODE_ENV === 'development'
      const isDevReactEnv = process.env.REACT_APP_ENVIRONMENT === 'development'
      const useLocalStrategy = (isDevNodeEnv && isDevReactEnv)
      if (useLocalStrategy) {
        const strategyServerUrl = getStrategyServerUrl(strategyId)
        const hostUrl = strategyServerUrl.split(':').slice(1).join(':')
        setStreamUrl(`ws:${hostUrl}/stream`)
        setApiConnected(true)
      } else {
        setStreamUrl(null)
        setApiConnected(false)
      }
    } else {
      const strategyServerUrl = getStrategyServerUrl(strategyId)
      const hostUrl = strategyServerUrl.split(':').slice(1).join(':')
      setStreamUrl(`wss:${hostUrl}/stream`)
      setApiConnected(true)
    }

    setAttemptedApiConnect(true)
    setIsLoading(false)
  }, [strategyId])

  const tradesComponent = useMemo(() => {
    const isDevNodeEnv = process.env.NODE_ENV === 'development'
    const isDevReactEnv = process.env.REACT_APP_ENVIRONMENT === 'development'
    const useLocalStrategy = (isDevNodeEnv && isDevReactEnv)

    if (isEmptyString(strategyId) && !useLocalStrategy) {
      return null
    }

    if (displayType == null || !(displayType === DISPLAY_TYPE.SIMPLE_NO_TRADES || displayType === DISPLAY_TYPE.DETAILED_NO_TRADES)) {
      return (
        <Grid
          item xs={6} md='auto'
          minWidth='min(100%, 50%, 400px)'
        >
          <Grid
            container
            direction='column'
            height={1}
            minWidth='min(100%, 50%, 400px)'
            maxWidth='400px'
          >
            <Grid item xs>
              <TradeCardList strategyId={strategyId} tradeUrl={streamUrl}/>
            </Grid>
          </Grid>
        </Grid>
      )
    } else {
      return null
    }
  }, [strategyId, displayType, streamUrl])

  const eventsComponent = useMemo(() => {
    const isDevNodeEnv = process.env.NODE_ENV === 'development'
    const isDevReactEnv = process.env.REACT_APP_ENVIRONMENT === 'development'
    const useLocalStrategy = (isDevNodeEnv && isDevReactEnv)

    if (isEmptyString(strategyId) && !useLocalStrategy) {
      return null
    } else {
      return (
        <Grid item xs width={1} height={0.5}>
          <EventDataGrid eventUrl={streamUrl}/>
        </Grid>
      )
    }
  }, [strategyId, streamUrl])

  const booksComponent = useMemo(() => {
    const isDevNodeEnv = process.env.NODE_ENV === 'development'
    const isDevReactEnv = process.env.REACT_APP_ENVIRONMENT === 'development'
    const useLocalStrategy = (isDevNodeEnv && isDevReactEnv)

    if (isEmptyString(strategyId) && !useLocalStrategy) {
      return null
    } else {
      return (
        <Grid item xs width={1} height={0.5}>
          <BookCardList bookUrl={streamUrl}/>
        </Grid>
      )
    }
  }, [strategyId, streamUrl])

  if (isLoading) {
    return (
      <Stack gap={2} padding={1} justifyContent='center'>
        <Typography color='primary' variant='h6' align='center'>Loading...</Typography>
        <Stack alignItems='center'><Loader/></Stack>
      </Stack>
    )
  }

  if (!apiConnected) {
    return (
      <Stack gap={2} padding={1} justifyContent='center'>
        <Typography color='primary' variant='h6' align='center'>Not Connected</Typography>
        <Stack alignItems='center'>
          {attemptedApiConnect ? <PowerOffIcon color='error' fontSize='large'/> : <Loader/>}
        </Stack>
        <Typography color='primary' align='center'>Strategy configuration is invalid</Typography>
      </Stack>
    )
  }

  return (
    <Paper
      square
      elevation={1}
      sx={{ height: 1, overflowY: 'auto' }}
    >
      <Grid
        container
        height={1}
        justifyContent='center'
      >
        <Grid
          item xs={12} md
          display='flex'
          alignItems='flex-start'
          justifyContent='stretch'
          minWidth='min(100%, 1000px)'
          sx={{
            height: {
              xs: 'revert',
              lg: 'calc(100dvh - 48px - 80px)',
            },
            overflowY: {
              xs: 'revert',
              lg: 'auto',
            },
          }}
        >
          <StrategyComponent strategyId={strategyId} strategyName={strategyName} displayType={displayType}/>
        </Grid>

        {tradesComponent}

        <Grid
          item xs={6} md='auto'
          minWidth='min(100%, 50%, 400px)'
        >
          <Grid
            container
            direction='column'
            minWidth='min(100%, 50%, 400px)'
            maxWidth='400px'
            sx={{
              height: {
                xs: '100dvh',
                lg: 'calc(100dvh - 48px - 80px)',
              },
            }}
          >
            { eventsComponent }
            { booksComponent }
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default StrategyTradeBookComposite

export const WrappedStrategyTradeBookComposite: React.FC<StrategyTradeBookLayoutProps> = (props) => {
  return (
    <StackRowLayout height='100%'>
      <StrategyTradeBookComposite {...props}/>
    </StackRowLayout>
  )
}
